<template>
    <div>
        <div v-if="editModeEnabled">
            <span v-for="(run_rate, index) in runRates" :key="index">
                <span class="row q-col-gutter-sm q-my-sm">
                    <date-time-picker
                        id="start-period-input"
                        v-model="run_rate.start"
                        :dense="true"
                        :noIcon="$q.screen.lt.md"
                        :label="$t('Valid from') + ' (' + localTimezone + ')'"
                        class="col-6 col-lg-12 col-xl-4"
                        readonly
                        disable
                    />

                    <date-time-picker
                        v-if="index !== runRates.length - 1"
                        id="start-period-input"
                        v-model="run_rate.end"
                        @update:modelValue="
                            updatedValue =>
                                handleEndDateChange(
                                    updatedValue,
                                    index,
                                    runRates
                                )
                        "
                        :noIcon="$q.screen.lt.md"
                        class="col-6 col-lg-12 col-xl-4"
                        :label="$t('Valid until') + ' (' + localTimezone + ')'"
                        :enableValidationRules="index !== runRates.length - 1"
                        dense
                    />
                    <date-time-picker
                        v-else
                        id="start-period-input"
                        :noIcon="$q.screen.lt.md"
                        :label="lastEndDateLabel"
                        class="col-6 col-lg-12 col-xl-4"
                        readonly
                        disable
                        dense
                    />
                    <q-input
                        data-test="run-rate-input"
                        v-model.number="run_rate.value"
                        type="number"
                        class="col"
                        :rules="[validateRunRate]"
                        :label="$t('Value')"
                        outlined
                        :hint="$t('Maximum number of products per minute')"
                        dense
                        color="accent"
                        bg-color="white"
                    />
                    <span>
                        <q-btn
                            v-if="index !== 0"
                            icon="fas fa-trash-alt"
                            class="col-2 col-md-1 q-mt-sm"
                            size="sm"
                            color="red"
                            @click="deleteProductRunRates(index)"
                            dense
                            flat
                        />
                    </span>
                    <span>
                        <q-btn
                            v-if="index === runRates.length - 1"
                            icon="fa-solid fa-circle-plus"
                            class="col-2 col-md-1 q-mt-sm"
                            size="sm"
                            @click="addNewExpectedRunRate()"
                            flat
                            dense
                        />
                    </span>
                </span>
            </span>
        </div>
        <div
            v-else
            class="q-mt-none"
            style="display: flex; justify-content: flex-start; column-gap: 20px"
        >
            {{ currentExpectedRunRate ? currentExpectedRunRate.value : '-' }}
        </div>
    </div>
</template>
<script>
import DateTimePicker from './DateTimePicker.vue';
import { convertUTCTimeToLocaleTimezone } from '../utils/time';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'ProductExpectedRunRates',
    components: { DateTimePicker },
    emits: ['changedRunRates'],
    props: {
        expectedRunRates: {
            type: Array,
            required: true
        },
        isEditMode: {
            type: Boolean,
            default: false
        }
    },
    watch: {
        expectedRunRates: {
            immediate: true,
            handler(value) {
                this.runRates = value;
            }
        },
        isEditMode: {
            immediate: true,
            handler(value) {
                this.editModeEnabled = value;
            }
        }
    },
    data() {
        return {
            localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            runRates: [],
            editModeEnabled: false,
            numberOfDaysForNewRunRate: 15
        };
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get the expected run rate for the current date
         * If the end date of the last run rate is null and the start date is in the past,
         * then the current expected run rate is the last one.
         * @returns {Object} The current expected run rate
         */
        currentExpectedRunRate() {
            const lastRunRate = this.runRates[this.runRates.length - 1];
            if (lastRunRate && lastRunRate.end === null) {
                const lastRunRateStart = new Date(lastRunRate.start);
                if (lastRunRateStart < new Date()) {
                    return lastRunRate;
                }
            }
            return this.runRates.find(runRate => {
                const runRateStart = new Date(runRate.start);
                const runRateEnd = new Date(runRate.end);
                return runRateStart <= new Date() && runRateEnd >= new Date();
            });
        },
        /**
         * Get last end date label
         * if the start date is in the future, then the end date label will be 'Future'
         * else, the end date label will be 'Present'
         * @returns {String} The label for the null end date
         */
        lastEndDateLabel() {
            const lastRunRate = this.runRates[this.runRates.length - 1];
            const lastRunRateStart = new Date(lastRunRate.start);
            if (lastRunRateStart > new Date()) {
                return this.$t('Future');
            }
            return this.$t('Present');
        }
    },
    methods: {
        convertUTCTimeToLocaleTimezone: convertUTCTimeToLocaleTimezone,
        /**
         * Add new expected run rate to the runRates
         */
        addNewExpectedRunRate() {
            let timeStampToInsert = new Date().toISOString();
            const lastRunRate = this.runRates[this.runRates.length - 1];
            if (this.runRates.length > 1) {
                const lastRunRateStart = new Date(lastRunRate.start);
                timeStampToInsert = new Date(
                    lastRunRateStart.setDate(
                        lastRunRateStart.getDate() +
                            this.numberOfDaysForNewRunRate
                    )
                ).toISOString();
            }
            const newRunRate = {
                start: timeStampToInsert,
                end: null,
                value: null
            };
            lastRunRate.end = timeStampToInsert;
            this.runRates.push(newRunRate);
            this.emitRunRatesChangedEvent();
        },
        /**
         * Synchronizes the start date of the next expected run rate
         * with the end date of the previous one.
         * @param {String} updatedValue
         * @param {Integer} index
         * @param {Array} expectedRunRates
         */
        handleEndDateChange(updatedValue, index, expectedRunRates) {
            if (index + 1 < expectedRunRates.length) {
                expectedRunRates[index + 1].start = updatedValue;
            }
        },
        /**
         * Delete the run rate from the runRates array at supplied index
         * @param {integer} indexToDelete: index of the run rate object to delete
         */
        deleteProductRunRates(indexToDelete) {
            if (this.runRates.length > 1) {
                this.runRates[indexToDelete - 1].end =
                    this.runRates[indexToDelete].end;

                this.runRates.splice(indexToDelete, 1);

                // set the end date of the last element to null
                if (indexToDelete === this.runRates.length - 2) {
                    this.runRates[this.runRates.length - 1].end = null;
                }
            }
            this.emitRunRatesChangedEvent();
        },
        /**
         * Emit run rates changed event
         */
        emitRunRatesChangedEvent() {
            this.$emit('changedRunRates', this.runRates);
        },
        /**
         * Validate run rate value
         * @param {String} value
         * @return {Boolean|String} True if the value is valid, error message otherwise
         */
        validateRunRate(value) {
            value = parseFloat(value);
            if (!isNaN(value)) {
                if (value <= 0) {
                    return this.$t('Run rate must be positive');
                }
            } else {
                return this.$t('Enter a positive real number');
            }
            return true;
        }
    }
};
</script>
